// RatePage.tsx — the rating interface.
//
// Two faces and three answers. The page is deliberately sparse: anything else
// on screen is a confound, and the one thing a rater should be doing is
// looking at the photographs.
//
// Full-bleed with the two faces as flex tracks (DESIGN.md C-LAYOUT-1/3), since
// the images are the content and every pixel of width is one more pixel of
// face. Only the explanatory copy keeps a measure.
import { useState, useEffect, useCallback, useRef } from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import { BsArrowLeft, BsArrowRight, BsPauseFill, BsCompass } from 'react-icons/bs'
import { getNextPair, submitComparison, type NextPair, type Outcome } from '../api'

const RatePage = () => {
    const [pair, setPair] = useState<NextPair | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    // Submitting is tracked so a fast rater cannot double-answer one pair: the
    // keyboard shortcuts make that easy to do by accident, and the model would
    // count the same judgement twice.
    const [submitting, setSubmitting] = useState(false)
    const [judged, setJudged] = useState(0)
    // When the current pair was rendered, so the answer carries a latency. A
    // ref rather than state: it must not trigger a re-render, and it has to be
    // readable from the keydown handler without being a dependency.
    const shownAt = useRef<number>(Date.now())

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const next = await getNextPair()
            setPair(next)
            setJudged(next.judged)
            shownAt.current = Date.now()
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not load a pair')
            setPair(null)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { void load() }, [load])

    const answer = useCallback(async (outcome: Outcome) => {
        if (!pair || submitting) return
        setSubmitting(true)
        try {
            const res = await submitComparison(
                pair.left.id, pair.right.id, outcome, Date.now() - shownAt.current)
            setJudged(res.judged)
            await load()
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not record that')
        } finally {
            setSubmitting(false)
        }
    }, [pair, submitting, load])

    // Keyboard: left / = / right. A rater doing a few hundred of these should
    // not have to move a mouse, and the arrow keys map to the layout.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return
            switch (e.key) {
                case 'ArrowLeft': e.preventDefault(); void answer(1); break
                case 'ArrowRight': e.preventDefault(); void answer(-1); break
                case '=':
                case 'ArrowDown':
                case ' ': e.preventDefault(); void answer(0); break
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [answer])

    if (loading && !pair) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading a pair…</span>
                </Spinner>
            </div>
        )
    }

    if (error && !pair) {
        return (
            <div className="prose-column">
                <Alert variant="warning">
                    <Alert.Heading className="fs-6">Nothing to rate</Alert.Heading>
                    {error}
                </Alert>
            </div>
        )
    }

    if (!pair) return null

    return (
        <div>
            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-3">
                <div>
                    <h1 className="fs-4 mb-1">Which do you find more attractive?</h1>
                    <p className="text-body-secondary small mb-0 prose-column">
                        Pick one, or <kbd>=</kbd> if they are the same to you. There is no
                        right answer and you can be shown the same two again — disagreeing
                        with yourself is useful information, not a mistake.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2 text-nowrap">
                    <Badge bg="secondary" title="Comparisons you have made">
                        {judged} judged
                    </Badge>
                    {/* The acquisition score, shown plainly. A rater is
                        spending their attention; they can see what the question
                        is expected to be worth. */}
                    {pair.exploring ? (
                        <Badge bg="secondary" title="Drawn at random for coverage">
                            <BsCompass className="me-1" /> exploring
                        </Badge>
                    ) : (
                        <Badge bg="secondary" title="Expected information gain, in nats">
                            {pair.infoGain.toFixed(3)} nats
                        </Badge>
                    )}
                </div>
            </div>

            <div className="rate-arena">
                <FaceChoice
                    subject={pair.left}
                    side="left"
                    disabled={submitting}
                    onPick={() => void answer(1)}
                />
                <div className="rate-tie">
                    <Button
                        variant="outline-secondary"
                        className="d-flex align-items-center gap-2"
                        disabled={submitting}
                        onClick={() => void answer(0)}
                        title="Equally attractive (=)"
                    >
                        <BsPauseFill style={{ transform: 'rotate(90deg)' }} /> Equal
                    </Button>
                    <span className="small text-body-secondary text-center">
                        <kbd>←</kbd> <kbd>=</kbd> <kbd>→</kbd>
                    </span>
                </div>
                <FaceChoice
                    subject={pair.right}
                    side="right"
                    disabled={submitting}
                    onPick={() => void answer(-1)}
                />
            </div>

            {error && (
                <Alert variant="danger" className="mt-3 prose-column" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}
        </div>
    )
}

interface FaceChoiceProps {
    subject: { id: string; imageUrl: string; sourceUrl: string; license: string }
    side: 'left' | 'right'
    disabled: boolean
    onPick: () => void
}

// One of the two options. The whole card is the hit target — asking someone to
// find a small button under a photograph is friction on the one action this
// page exists for.
const FaceChoice = ({ subject, side, disabled, onPick }: FaceChoiceProps) => (
    <Card
        className={`rate-face rate-face-${side}`}
        role="button"
        tabIndex={0}
        aria-label={`Choose the ${side} person`}
        onClick={() => !disabled && onPick()}
        onKeyDown={e => {
            if (e.key === 'Enter') {
                e.preventDefault()
                if (!disabled) onPick()
            }
        }}
    >
        <div className="rate-face-image">
            {/* Keyed on the subject id so React swaps the element rather than
                mutating src — without it the browser keeps painting the old
                photo until the new one decodes, and a rater answers the pair
                they were looking at a moment ago. */}
            <img key={subject.id} src={subject.imageUrl} alt="" loading="eager" />
        </div>
        <Card.Footer className="d-flex justify-content-between align-items-center gap-2">
            <Button
                variant="outline-primary"
                size="sm"
                disabled={disabled}
                className="d-flex align-items-center gap-1"
                onClick={e => { e.stopPropagation(); onPick() }}
            >
                {side === 'left' ? <><BsArrowLeft /> This one</> : <>This one <BsArrowRight /></>}
            </Button>
            {/* Provenance when the ingest recorded it, nothing when it did
                not. Both fields are optional — the admin role is what vouches
                for the corpus — so this renders a link, a bare licence string,
                or an empty slot, and never a dead link. */}
            {subject.sourceUrl ? (
                <a
                    href={subject.sourceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="small text-body-secondary text-truncate"
                    title={subject.license
                        ? `${subject.license} — ${subject.sourceUrl}`
                        : subject.sourceUrl}
                    onClick={e => e.stopPropagation()}
                >
                    {subject.license || 'source'}
                </a>
            ) : (
                <span className="small text-body-secondary text-truncate">
                    {subject.license}
                </span>
            )}
        </Card.Footer>
    </Card>
)

export default RatePage
